import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import {
  assignCustomer,
  CASE_GONE_SENTENCE,
  CASE_KEY,
  CASES_KEY,
  caseGone,
  ProblemError,
  type CustomerOut,
} from "@/api";
import { Button } from "@/components/Button";
import { DialogFooter } from "@/components/Dialog";
import { StateMessage } from "@/components/StateMessage";
import { CustomerForm, type CustomerDraft } from "@/features/customer/CustomerForm";
import { CustomerSearch } from "@/features/customer/CustomerSearch";

import styles from "./Record.module.css";

function messageOf(cause: unknown, what: string): string {
  return cause instanceof ProblemError ? cause.userMessage : `${what} — Server nicht erreichbar.`;
}

interface CustomerFieldProps {
  caseId: string;
  onDone: () => void;
  /** Not called while a customer is being written — the answer would be lost (record fix round 2, I-2). */
  onCancel?: () => void;
  /** A request is in flight, or not any more — the dialog around holds Escape meanwhile (I-2). */
  onBusy?: (busy: boolean) => void;
}

/**
 * „Suchen oder neu anlegen" (design §8.2): one field over the shared
 * customer master (D-3), in a dialog (UX ruling 9). The search itself —
 * the ARIA combobox, its keys and its status line — is `CustomerSearch`,
 * shared with the Eingang (spec 2026-10-20, Task 10); this field assigns
 * what is picked, and writes a new customer where none fits.
 *
 * A new customer is written in `CustomerForm` (spec 2026-10-20): the form
 * warns about an e-mail address that somebody has already — addresses may
 * repeat (D-7) — and refuses a customer number that is taken, and either way
 * offers the customer who has it; whoever it hands back is assigned here.
 *
 * Record fix wave (T10, I1): while a new customer is written the search is
 * put away: the name stood twice. What was typed stays here, so the search
 * comes back with it. Buttons that are working keep the focus.
 *
 * Record fix round 2: the busy state goes up to the dialog, which then keeps
 * Escape from closing on a request in flight (I-2).
 *
 * A Vorgang handed over while the dialog was open answers 404 on the
 * assignment (final review of Plan D, I-3): the Vorgang is read again — the
 * record turns into „nicht gefunden" — and until then the field says why and
 * assigns nobody more. „Abbrechen" stays.
 */
export function CustomerField({ caseId, onDone, onCancel, onBusy }: CustomerFieldProps) {
  const client = useQueryClient();
  const id = useId();
  const comboRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  // What the typed search text was: the name, or the address when it has an „@".
  const [initial, setInitial] = useState<Partial<CustomerDraft>>({});
  const [refused, setRefused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [gone, setGone] = useState(false);

  // Back into the search when the form closes — the control that had the
  // focus is gone. (The form takes the focus into itself when it opens.) Only
  // on a real change of `creating`: under `<StrictMode>` the effect runs
  // twice on mount, and a skip-the-first-run flag let the second run focus
  // the search — the dialog around then took it for its opener (record fix
  // round 2, I-3).
  const wasCreating = useRef(creating);
  useEffect(() => {
    if (wasCreating.current === creating) return;
    wasCreating.current = creating;
    if (!creating) comboRef.current?.focus();
  }, [creating]);

  function hold(on: boolean) {
    setBusy(on);
    onBusy?.(on);
  }

  function cancel() {
    if (!busy) onCancel?.();
  }

  async function assign(customerId: string) {
    hold(true);
    setRefused(null);
    try {
      await assignCustomer(caseId, customerId);
      // The record and the Vorgang's facts; the register and the start page
      // name the customer too — the whole `["cases"]` prefix (M-5).
      void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      void client.invalidateQueries({ queryKey: CASES_KEY });
      onDone();
    } catch (cause) {
      hold(false);
      if (caseGone(cause)) {
        setGone(true);
        setRefused(CASE_GONE_SENTENCE);
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        return;
      }
      setRefused(messageOf(cause, "Kunde nicht zugeordnet"));
    }
  }

  function pick(customer: CustomerOut) {
    if (busy || gone) return;
    void assign(customer.id);
  }

  function create(text: string) {
    if (busy || gone) return;
    setInitial(text.includes("@") ? { email: text } : { displayName: text });
    setRefused(null);
    setCreating(true);
  }

  const refusedId = `${id}-refused`;

  return (
    <div className={styles.combo}>
      {creating ? null : (
        <CustomerSearch
          label="Kunde suchen oder neu anlegen"
          query={query}
          onQueryChange={setQuery}
          onPick={pick}
          onCreate={create}
          onEscape={cancel}
          busy={busy}
          held={gone}
          inputRef={comboRef}
        />
      )}
      {/* A refusal of the assignment in the one notice pattern (final review
          of Plan D) — red edge, symbol and word, and the `role="alert"` of
          the search. The form's own refusals stand inside the form. */}
      {refused !== null ? <StateMessage id={refusedId} tone="conflict" live="alert" title={refused} /> : null}
      {creating ? (
        <CustomerForm
          initial={initial}
          // Taken, or taken over from the master: either way it is assigned.
          onCreated={(customer) => assign(customer.id)}
          onCancel={() => {
            setRefused(null);
            setCreating(false);
          }}
          onBusy={hold}
          disabled={gone}
          submitDescribedBy={gone ? refusedId : undefined}
          submitLabel="Anlegen und zuordnen"
          cancelLabel="Zurück zur Suche"
        />
      ) : onCancel !== undefined ? (
        <DialogFooter>
          <Button onClick={cancel} aria-disabled={busy ? "true" : undefined}>
            Abbrechen
          </Button>
        </DialogFooter>
      ) : null}
    </div>
  );
}
